import '../shell/assets/main.css';
import '../shell/assets/tailwind.css';
// Expose the shared runtime singletons on globalThis (见 12 / 18) before any
// runtime-loaded module UI imports them through the `/_mod/_runtime/*` shims the
// page's import map points at. Must run early; generated at build time. The
// Electron renderer does the same against `hibro-assets://_runtime/*`.
import 'virtual:hibro-runtime-globals';

import type { IMessageTransport } from '@hibro/platform-core';
import type {
  InAppNotificationBridge,
  InlineDetachedStore,
  LoadUi,
  LogBridge,
} from '@hibro/platform-renderer';
import { parseSurfaceTarget } from '@hibro/platform-renderer';
import type {
  BrowserOpenOptions,
  InAppNotificationMessage,
  LogInput,
  LogQuery,
  LogRecord,
  ResolvedNotification,
  RpcMessage,
} from '@hibro/shared';
import {
  APP_SCHEME,
  BROWSER_EDITOR_VIEW_ID,
  describeLinkForLog,
  parseModuleLink,
  shouldPlayNotificationChime,
  webOsNotificationDelivery,
} from '@hibro/shared';
import { webModules } from 'virtual:hibro-web-modules';

import { watchHostLiveness } from '../ext-host-web/host-liveness';
import { resolveBrowserOs } from '../ext-host-web/host-os';
import type {
  DetachPlacement,
  HostToPageMessage,
  PageToHostMessage,
} from '../ext-host-web/protocol';
import { connectionLockName, isControlMessage } from '../ext-host-web/protocol';
import { bootShell } from '../shell/boot';
import { stampHostIdentity } from '../shell/host-identity';
import { createWebIframeBridge } from '../shell/iframe-bridge';
import { deliverViewNavigation } from '../shell/navigation-registry';
import { deliverViewTitle } from '../shell/view-title-registry';
import { createAttentionPainter } from './attention-painter';
import { createLocalStorageBrowserEditorTabCatalog } from './browser/browser-editor-tab-catalog';
import { createBrowserWindowHandoff } from './browser/browser-window-handoff';
import {
  hasLiveUserActivation,
  presentUnownedLink,
} from './browser/unowned-link';
import {
  createWebBrowserHost,
  detachedEditorUrlFromPayload,
} from './browser/web-browser-host';
import { createWebUserActivationApi } from './browser/web-user-activation';
import { createWebClipboard } from './clipboard';
import { holdConnectionLock } from './connection-lock';
import {
  adoptFocusedDetachedWindow,
  adoptOpenedDetachedWindow,
} from './detached-window';
import { decideGenerationAction } from './generation';
import { createLinkedModules } from './linked-modules';
import { downloadHibroLogFile, listHibroLogFiles } from './log-files';
import { createPageLogSink } from './log-sink';
import { canPickLocalBundle, pickLocalBundle } from './pick-local-bundle';
import { decideReconnect, RECONNECT_STABLE_MS } from './reconnect';
import { createStartupUrlBarrier } from './startup-url-barrier';
import { decideSwControlAction } from './sw-control';

/**
 * Browser runtime entry (见 18 Web 运行端). The desktop counterpart is
 * `src/renderer/src/main.tsx`; both hand off to the shared {@link bootShell} and
 * differ only in how the extension-host transport is obtained and which
 * OS-facing APIs exist.
 *
 * This page plays the role the Electron **main process** plays on desktop: it
 * owns the things a worker cannot see (the dark-mode media query, the log sink)
 * and connects to the SharedWorker that hosts the modules.
 */

/**
 * `/_sw.js` — kept as a literal (not imported from `scripts/web-service-worker`)
 * so the page graph never pulls Vite's programmatic `build` into the client
 * bundle. Must stay in lockstep with that script's `WEB_SERVICE_WORKER_PATH`.
 */
const STORAGE_SERVICE_WORKER_URL = '/_sw.js';

/**
 * Page-side log sink (见 15 / 18). The desktop sink lives in the main process,
 * above the extension host so it outlives the ext-host crashes it records; the
 * browser's outermost surviving layer is this page, so it lives here and the
 * SharedWorker forwards its records over the port.
 */
const logSink = createPageLogSink();

/**
 * Login-bounce diagnosis only. Web has no `mirrorConsole`, and leaving for the
 * account center unloads this page, which clears DevTools. These lines are kept
 * in this tab's sessionStorage and printed again on the next load of this
 * origin. Error-level matches still use `console.error` below, and are kept too.
 */
const LOGIN_BOUNCE_LOG_KEY = 'hibro.login-bounce-log';
const LOGIN_BOUNCE_LOG_LIMIT = 40;
const LOGIN_BOUNCE_CONSOLE_MESSAGES = new Set([
  'web: startup url delivering',
  'web: startup url delivered',
  'web: startup url delivery skipped',
  'account: login callback accepted',
  'account: web login callback failed',
  'account: login callback failed',
  'account: OAuth callback returned error',
  'onboarding: web login redirect',
]);

interface LoginBounceLogEntry {
  ts: number;
  level: LogRecord['level'];
  message: string;
  detail: unknown;
}

function isLoginBounceLogEntry(value: unknown): value is LoginBounceLogEntry {
  if (!value || typeof value !== 'object') return false;
  const entry = value as Partial<LoginBounceLogEntry>;
  return (
    typeof entry.ts === 'number' &&
    typeof entry.level === 'string' &&
    typeof entry.message === 'string' &&
    'detail' in entry
  );
}

function readLoginBounceLog(): LoginBounceLogEntry[] {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(LOGIN_BOUNCE_LOG_KEY);
  } catch (err) {
    console.error(
      '[hibro] login bounce log could not be read',
      err instanceof Error ? err.message : String(err),
    );
    return [];
  }
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed) || !parsed.every(isLoginBounceLogEntry)) {
      throw new Error('login bounce log is not an entry list');
    }
    return parsed;
  } catch (err) {
    console.error(
      '[hibro] login bounce log could not be read',
      err instanceof Error ? err.message : String(err),
    );
    return [];
  }
}

function rememberLoginBounce(entry: LoginBounceLogEntry): void {
  const kept = readLoginBounceLog();
  kept.push(entry);
  try {
    sessionStorage.setItem(
      LOGIN_BOUNCE_LOG_KEY,
      JSON.stringify(kept.slice(-LOGIN_BOUNCE_LOG_LIMIT)),
    );
  } catch (err) {
    console.error(
      '[hibro] login bounce log could not be kept',
      err instanceof Error ? err.message : String(err),
    );
  }
}

function printLoginBounce(entry: LoginBounceLogEntry): void {
  if (entry.level === 'error') {
    console.error('[hibro]', entry.message, entry.detail);
    return;
  }
  console.info('[hibro]', entry.message, entry.detail);
}

function replayLoginBounceLog(): void {
  for (const entry of readLoginBounceLog()) {
    printLoginBounce(entry);
  }
}

function mirrorLoginBounceToConsole(record: {
  level: LogRecord['level'];
  message: string;
  data?: unknown;
  error?: { message: string };
}): void {
  if (!LOGIN_BOUNCE_CONSOLE_MESSAGES.has(record.message)) return;
  const entry: LoginBounceLogEntry = {
    ts: Date.now(),
    level: record.level,
    message: record.message,
    detail:
      record.level === 'error'
        ? (record.error?.message ?? '')
        : (record.data ?? ''),
  };
  rememberLoginBounce(entry);
  if (record.level === 'error') return;
  printLoginBounce(entry);
}

function isLoginTraceRecord(message: string): boolean {
  return (
    LOGIN_BOUNCE_CONSOLE_MESSAGES.has(message) ||
    message.includes('/v1/oauth/state') ||
    message.includes('/api/v1/auth/token/')
  );
}

/** Local Vite only. A deployed origin has no sink, so other hosts stay quiet. */
function forwardLoginTrace(record: LogRecord): void {
  const host = window.location.hostname;
  if (host !== 'localhost' && host !== '127.0.0.1') return;
  if (!isLoginTraceRecord(record.message)) return;
  void fetch('/_dev/login-trace', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ts: record.ts,
      level: record.level,
      message: record.message,
      data: record.data,
      ...(record.error ? { error: { message: record.error.message } } : {}),
    }),
    keepalive: true,
  }).catch((err: unknown) => {
    console.error(
      '[hibro] login trace could not be stored',
      err instanceof Error ? err.message : String(err),
    );
  });
}

replayLoginBounceLog();

function appendLog(record: LogRecord): void {
  logSink.append(record);
  // Console fallback for errors. `LogConsoleMirror` would normally cover this,
  // but it is gated on `mirrorConsole`, which only ever arrives through the
  // Electron-only `window.hibroDevtools` — so on web the mirror never mounts and
  // removing this would leave errors with no console trace at all. Revisit if the
  // devtools/diagnostics toggle ever gets a web counterpart (it would double-log).
  //
  // Records that came in through `window.hibroLog` deliberately bypass this (see
  // {@link appendFromPage}): their author runs in this very context and already
  // owns its console output — module UI keeps a dev-only mirror of its own — so
  // printing here too would just double up. The login-bounce lines are the
  // exception: nothing else prints them, and the page is about to unload.
  mirrorLoginBounceToConsole(record);
  forwardLoginTrace(record);
  if (record.level === 'error') {
    console.error('[hibro]', record.message, record.error ?? '');
  }
}

/**
 * Append a record authored **in this page** — the renderer half of the log
 * pipeline (见 15). Mirrors what the desktop's main process does for
 * `hibro:log:append`: fills the `ts` default and stamps `source: 'renderer'` so a
 * page cannot claim to be a platform process.
 */
/**
 * Renderer records waiting for a live host port. `post()` drops messages when
 * `currentPort` is unset; this queue is what gets them into the daily file
 * after the SharedWorker connects (见 15).
 */
const pendingPageLogs: LogRecord[] = [];
let logPort: MessagePort | undefined;

/** How long a download waits for the host to finish its append batch. */
const LOG_FLUSH_TIMEOUT_MS = 8000;

const logFlushWaiters = new Map<
  number,
  {
    resolve: () => void;
    reject: (err: Error) => void;
    timer: ReturnType<typeof setTimeout>;
  }
>();
let logFlushSeq = 0;

function setLogPort(port: MessagePort | undefined): void {
  logPort = port;
  if (port) {
    flushPendingPageLogs();
  }
}

function flushPendingPageLogs(): void {
  const port = logPort;
  if (!port || pendingPageLogs.length === 0) {
    return;
  }
  const records = pendingPageLogs.splice(0);
  port.postMessage({
    type: 'hibro:log-append',
    records,
  } satisfies PageToHostMessage);
}

function settleLogFlush(requestId: number): void {
  const waiter = logFlushWaiters.get(requestId);
  if (!waiter) {
    return;
  }
  clearTimeout(waiter.timer);
  logFlushWaiters.delete(requestId);
  waiter.resolve();
}

function failLogFlushes(reason: string): void {
  for (const [requestId, waiter] of logFlushWaiters) {
    clearTimeout(waiter.timer);
    logFlushWaiters.delete(requestId);
    waiter.reject(new Error(reason));
  }
}

/** Ask the host to commit the current batch. Rejects when no host is up. */
function requestLogFlush(): Promise<void> {
  const port = logPort;
  if (!port) {
    return Promise.reject(
      new Error('log file flush failed: extension host is not connected'),
    );
  }
  flushPendingPageLogs();
  const requestId = ++logFlushSeq;
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      if (!logFlushWaiters.delete(requestId)) {
        return;
      }
      reject(new Error('log file flush timed out'));
    }, LOG_FLUSH_TIMEOUT_MS);
    logFlushWaiters.set(requestId, { resolve, reject, timer });
    port.postMessage({
      type: 'hibro:log-flush',
      requestId,
    } satisfies PageToHostMessage);
  });
}

function appendFromPage(input: LogInput): void {
  const record: LogRecord = {
    ...input,
    ts: input.ts ?? Date.now(),
    source: 'renderer',
  };
  logSink.append(record);
  mirrorLoginBounceToConsole(record);
  forwardLoginTrace(record);
  pendingPageLogs.push(record);
  flushPendingPageLogs();
}

/**
 * Show a detached surface (§5.8 通用 Detachable), preferring a window of its own.
 *
 * The worker cannot open one — no `window` — so it asks a page, the same split as
 * `openExternal` and `notifyOS`. `name` is derived from the detach handle, which
 * makes the call idempotent at the browser level too: re-opening the same name
 * reuses that window rather than spawning another.
 *
 * A popup needs transient user activation, which a module-initiated detach (an
 * incoming call) has nothing to draw on. That used to be the end of it — the
 * surface simply never appeared. Instead the surface falls back **in-page**: the
 * workbench floats it in a frame of its own, which is a real placement the host
 * can record, close and focus (见 18 弹窗拦截). `null` is left for the one case
 * neither works: no workbench mounted in this page to float it in.
 */
function showDetachedSurface(
  request: Extract<HostToPageMessage, { type: 'hibro:detach-open' }>,
  inlineDetached: InlineDetachedStore,
): DetachPlacement | null {
  const { name, url, features, geometry } = request;
  const opened = window.open(url, name, features);
  const adoption = adoptOpenedDetachedWindow(opened, url);
  if (adoption === 'window') {
    opened?.focus();
    return 'window';
  }
  // Only a page showing the workbench has somewhere to float a surface. This one
  // is itself a detached surface, so it says so instead of accepting a surface it
  // would never render.
  if (parseSurfaceTarget(window.location.search)) {
    appendFromPage({
      level: 'warn',
      kind: 'log',
      message:
        'detached window blocked, and this page is a detached surface itself; nothing shown',
      data: { name },
    });
    return null;
  }
  // The surface's identity travels in the URL the driver built, so the in-page
  // placement reads it back rather than inventing a second payload shape.
  const target = parseSurfaceTarget(new URL(url).search);
  if (!target) {
    appendFromPage({
      level: 'error',
      kind: 'log',
      message: 'detach failed: the surface URL named no target',
      data: { name },
    });
    return null;
  }
  appendFromPage({
    level: 'info',
    kind: 'log',
    message:
      'detached window blocked by the browser (popups need a user gesture); hosting it in-page instead',
    data: { name },
  });
  inlineDetached.mount({
    id: name,
    viewId: target.viewId,
    moduleId: target.moduleId,
    ...(target.fragmentId ? { fragmentId: target.fragmentId } : {}),
    ...(target.instanceKey ? { instanceKey: target.instanceKey } : {}),
    ...(target.payload !== undefined ? { payload: target.payload } : {}),
    ...geometry,
  });
  return 'inline';
}

/** Last-resort raise via window name. Prefer `hibro:detach-focus-self`. */
function focusDetachedWindow(name: string): void {
  const existing = window.open('', name);
  const adoption = adoptFocusedDetachedWindow(existing);
  if (adoption === 'focused') {
    return;
  }
  appendFromPage({
    level: 'warn',
    kind: 'log',
    message:
      existing && !existing.closed
        ? 'focusing a detached window found no surface; left the named page alone'
        : 'focusing a detached window missed (no live surface; blank tab closed if spawned)',
    data: { name },
  });
}

/**
 * Register the `@storage` Service Worker as early as possible (见 18). Activation
 * is async (`skipWaiting` + `clients.claim`); requests that race ahead of it
 * hit the static route's honest 404. Media traffic happens well after activate
 * in practice, so boot is not blocked on `ready`.
 */
function registerStorageServiceWorker(): void {
  if (!('serviceWorker' in navigator)) {
    appendFromPage({
      level: 'warn',
      kind: 'log',
      message:
        'navigator.serviceWorker unavailable; @storage media URLs will 404',
    });
    return;
  }
  void navigator.serviceWorker
    .register(STORAGE_SERVICE_WORKER_URL)
    .then(recoverServiceWorkerControl)
    .catch((err: unknown) => {
      const reason = err instanceof Error ? err.message : String(err);
      appendFromPage({
        level: 'warn',
        kind: 'log',
        message: `Service Worker registration failed: ${reason}`,
      });
    });
}

/**
 * Bring a document the Service Worker does not control back under it (见 18).
 *
 * A force reload leaves the page uncontrolled for its whole lifetime, which on
 * this runtime means every `@storage` URL and every delivered/side-loaded
 * module's asset 404s for the rest of the session while the rest of the app looks
 * fine. `decideSwControlAction` owns the rule (including the breaker that keeps
 * the reload from looping); this half performs it and says what happened.
 *
 * Reported through {@link logShell} rather than {@link appendFromPage} so the
 * `error` also lands in the console: the condition is invisible in the UI, and
 * the diagnostics panel is not where anyone looks first.
 */
function recoverServiceWorkerControl(
  registration: ServiceWorkerRegistration,
): void {
  const action = decideSwControlAction({
    controlled: navigator.serviceWorker.controller !== null,
    hasActiveWorker: registration.active !== null,
    store: sessionStore(),
    now: Date.now(),
  });
  if (action === 'reload') {
    window.location.reload();
    return;
  }
  if (action === 'recovered') {
    // Said here rather than before the reload above, which would have taken the
    // record with it: this is the load that can still be read.
    logShell(
      'info',
      'regained @storage Service Worker control by reloading; this document had been force-reloaded, which bypasses it',
    );
    return;
  }
  if (action === 'stuck') {
    logShell(
      'error',
      'the @storage Service Worker does not control this document and reloading did not fix it; ' +
        '@storage media and delivered/linked module assets will 404 for this session. Reload normally (F5).',
    );
  }
}

/** `sessionStorage` where reachable; absent in the odd hardened profile. */
function sessionStore(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

registerStorageServiceWorker();

// Expose the ring for **programmatic** reads — automated verification and ad-hoc
// console scripts. The diagnostics panel does not use this (it goes through the
// bridge below); this stays because reading logs from a script has no other route.
(globalThis as unknown as { __hibroLogs: LogRecord[] }).__hibroLogs =
  logSink.records;

function logShell(level: LogRecord['level'], message: string): void {
  appendLog({
    ts: Date.now(),
    level,
    kind: 'log',
    source: 'renderer',
    scope: 'renderer',
    message,
  });
}

window.addEventListener('error', (event) => {
  appendLog({
    ts: Date.now(),
    level: 'error',
    kind: 'crash',
    source: 'renderer',
    scope: 'renderer',
    message: `Renderer error: ${event.message}`,
    error:
      event.error instanceof Error
        ? { message: event.error.message, stack: event.error.stack }
        : { message: String(event.message) },
  });
});
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason as unknown;
  const err = reason instanceof Error ? reason : new Error(String(reason));
  appendLog({
    ts: Date.now(),
    level: 'error',
    kind: 'crash',
    source: 'renderer',
    scope: 'renderer',
    message: `Renderer unhandled rejection: ${err.message}`,
    error: { message: err.message, stack: err.stack },
  });
});

/**
 * Same discovery as the desktop shell, but from the generated module set rather
 * than a glob (见 18): only modules declaring `runtimes: ["web"]` are in the
 * browser build at all, and Vite requires literal glob patterns. Keyed by manifest
 * id — the key `UiRegistry` looks up — which the generator knows and a glob over
 * paths could only guess at from the directory name.
 */
function discoverUiLoaders(): Map<string, LoadUi> {
  const loaders = new Map<string, LoadUi>();
  for (const { id, loadUi } of webModules) {
    if (loadUi) {
      loaders.set(id, loadUi);
    }
  }
  return loaders;
}

// Host identity (见 18 / 01): the OS (so module UI picks the right per-OS variant
// instead of falling back to its default) and the release channel, whose resolver
// is the same one the desktop main process uses, injected by `vite.web.config.ts`
// (`serve` → `dev`, `build` → `stable`). Stamped rather than injected because its
// consumers are module UI singletons that never see React props.
stampHostIdentity({
  os: resolveBrowserOs(),
  channel: import.meta.env.HIBRO_PRODUCT_CHANNEL,
});
if (typeof import.meta.env.HIBRO_APP_NAME === 'string') {
  document.title = import.meta.env.HIBRO_APP_NAME;
}

/**
 * This runtime's attention driver (见 23) — the counterpart of the desktop's
 * Dock badge and taskbar flash, drawn onto the tab's favicon and title.
 *
 * Created here, straight after the title is stamped and before anything can
 * change it: the painter captures that title as the one it prefixes a count
 * onto, so a later `(3) ` would otherwise end up baked into the base.
 */
const attentionPainter = createAttentionPainter({
  warn: (message, data) =>
    appendFromPage({
      level: 'warn',
      kind: 'log',
      message,
      ...(data ? { data } : {}),
    }),
});

/**
 * Reading half of the log pipeline (见 15). Shared by the diagnostics panel (which
 * gets it as an injected bridge) and `window.hibroLog` below — one sink, one set
 * of accessors, no IPC hop in either direction since the sink is in this context.
 *
 * `openFolder` is absent from both: a browser has no OS folder to reveal, and that
 * absence is what tells the panel to drop the button rather than offer one that
 * does nothing.
 */
const logReader = {
  query: (query?: LogQuery) => Promise.resolve(logSink.query(query)),
  clear: () => {
    logSink.clear();
    return Promise.resolve();
  },
  onChanged: (callback: (record: LogRecord) => void) =>
    logSink.onChanged(callback),
};

/** The panel's reader plus the writer platform-renderer logs through (见 15). */
const logBridge: LogBridge = {
  ...logReader,
  append: (record) => appendFromPage(record),
  listLogFiles: () => listHibroLogFiles(requestLogFlush),
  downloadLogFile: (name) => downloadHibroLogFile(name, requestLogFlush),
};

// The two remaining renderer-side platform globals (见 18 接缝表). Both belong to
// the platform's declared `window.hibro*` surface, and both were Electron-only
// until now — so module UI reaching for them got silence in a browser: log records
// vanished instead of reaching the diagnostics panel, and clipboard writes fell
// back to a copy of this logic kept inside the module.
window.hibroLog = {
  // Cast at the boundary, as `createElectronLogBridge` does: the global is typed
  // structurally and does not carry the closed `@hibro/shared` unions.
  append: (record) => appendFromPage(record as LogInput),
  ...logReader,
};
window.hibroClipboard = createWebClipboard(logShell);

const container = document.getElementById('root');

if (!container) {
  throw new Error('Root container #root was not found in the document');
}

// Module SharedWorker is a hard requirement (见 18): classic workers have no
// dynamic `import()`, and the host's lazy module loading depends on it. Desktop
// browsers that matter all have it (Chrome 80+, Firefox 114+, Safari 16+); what
// we refuse to do is white-screen with an uncaught ReferenceError.
if (typeof SharedWorker === 'undefined') {
  container.textContent =
    'This browser does not support SharedWorker, which HiBro Web requires. ' +
    'Please use a current desktop Chromium, Firefox, or Safari.';
} else {
  bootWebRuntime(container);
}

function bootWebRuntime(container: HTMLElement): void {
  /** How long to wait for `hibro:ready` before treating the host as dead. */
  const HOST_READY_TIMEOUT_MS = 8000;

  /**
   * Which build this page came from (见 18 版本换代), stamped in by the web config
   * alongside the host's copy of the same value.
   */
  const PAGE_BUILD_ID: string = import.meta.env.HIBRO_BUILD_ID;

  /**
   * Live worker port. Mutable so a reconnect swaps it under the bridges that were
   * closed over at boot (iframe relay, control-plane `post`).
   */
  let currentPort: MessagePort | undefined;
  let readyTimer: ReturnType<typeof setTimeout> | undefined;
  /** True until the first successful `hibro:ready`; reconnects never re-deliver a deep link. */
  let initialHandshake = true;
  let disposed = false;
  /**
   * Reconnect attempts since this page was last connected (见 `decideReconnect`).
   * A replacement host must remain alive for `RECONNECT_STABLE_MS` before this
   * resets: `hibro:ready` precedes storage probes and startup activation, so it
   * cannot distinguish a healthy host from one that attaches and immediately
   * dies.
   */
  let reconnectAttempt = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  let reconnectStableTimer: ReturnType<typeof setTimeout> | undefined;

  function post(
    message: PageToHostMessage,
    transfer: Transferable[] = [],
  ): void {
    currentPort?.postMessage(message, transfer);
  }

  /**
   * Workers this page runs on behalf of a module's service (见 18), by the id the
   * host allocated. Kept so the host can terminate one on request; a worker whose
   * page goes away needs no bookkeeping here — the browser tears it down with the
   * document, and the host learns of it from this connection's Web Lock.
   *
   * The *host* going away is a different matter and is handled explicitly, in
   * {@link discardHostedWorkers}.
   */
  const hostedWorkers = new Map<number, Worker>();
  const hostedWorkerModules = new Map<number, string>();
  let spawnQueue = Promise.resolve();

  /**
   * Perform `context.worker.spawn` for the host (见 18).
   *
   * The host cannot construct a worker at all — its SharedWorker global has
   * neither `Worker` nor `SharedWorker` — so a document does it and hands back a
   * channel. Once the two ends are transferred this page is **out of the data
   * path**: the module's service and its worker talk directly.
   *
   * The first message to the worker carries the port and nothing else. That
   * absence is deliberate: a module's worker then only implements the ordinary
   * web idiom (`self.onmessage = (e) => e.ports[0]`) and needs no knowledge of
   * this platform.
   *
   * `exclusive` is the exception. The page asks that module's previous worker
   * to release its OPFS sync access handle and waits for the ack before
   * `new Worker`. `terminate` alone leaves the handle.
   */
  function releaseHostedWorker(
    worker: Worker,
  ): Promise<'released' | 'timeout' | 'unreachable'> {
    return new Promise((resolve) => {
      let settled = false;
      let timer = 0;
      const finish = (
        outcome: 'released' | 'timeout' | 'unreachable',
      ): void => {
        if (settled) {
          return;
        }
        settled = true;
        window.clearTimeout(timer);
        worker.onmessage = null;
        resolve(outcome);
      };
      timer = window.setTimeout(() => finish('timeout'), 8000);
      worker.onmessage = (event: MessageEvent) => {
        const data = event.data as { t?: unknown } | null;
        if (data && data.t === 'released') {
          finish('released');
        }
      };
      try {
        worker.postMessage({ t: 'release-opfs' });
      } catch {
        finish('unreachable');
      }
    });
  }

  async function spawnModuleWorker(
    workerId: number,
    moduleId: string,
    url: string,
    exclusive = false,
  ): Promise<void> {
    if (exclusive) {
      // 直接 terminate 不会放开 sync access handle。先让上一只 worker 执行
      // sdk.free()，再拆掉它，然后才 new Worker。
      const previous = [...hostedWorkerModules.entries()]
        .filter(([, hostedModule]) => hostedModule === moduleId)
        .map(([id]) => id);
      for (const id of previous) {
        const worker = hostedWorkers.get(id);
        if (worker) {
          await releaseHostedWorker(worker);
        }
        terminateModuleWorker(id);
      }
    }
    let worker: Worker;
    try {
      worker = new Worker(url, { type: 'module' });
    } catch (err) {
      logShell(
        'warn',
        `worker for "${moduleId}" could not be constructed: ${err instanceof Error ? err.message : String(err)}`,
      );
      post({ type: 'hibro:worker-spawned', workerId, ok: false });
      return;
    }
    // Fires for a script that 404s or throws while loading — after this page has
    // already reported success, which is why the host treats a close as its own
    // event rather than part of the spawn result.
    worker.onerror = (event) => {
      const detail = event.message || 'unknown error';
      logShell('warn', `worker for "${moduleId}" failed: ${detail}`);
      terminateModuleWorker(workerId);
      // The message travels with the report, not just into this page's log: the
      // module's service runs in the host and is the side that decides what to
      // do about it, and correlating two logs by timestamp is not something it
      // should have to do.
      post({ type: 'hibro:worker-closed', workerId, detail });
    };
    const channel = new MessageChannel();
    worker.postMessage(null, [channel.port1]);
    hostedWorkers.set(workerId, worker);
    hostedWorkerModules.set(workerId, moduleId);
    post({ type: 'hibro:worker-spawned', workerId, ok: true }, [channel.port2]);
  }

  function terminateModuleWorker(workerId: number): void {
    const worker = hostedWorkers.get(workerId);
    hostedWorkers.delete(workerId);
    hostedWorkerModules.delete(workerId);
    worker?.terminate();
  }

  /**
   * Terminate every worker this page was hosting, because the host that owned
   * them is gone (见 18 连接生命周期).
   *
   * They cannot outlive it: each one exists to serve a module *service* that died
   * with the host, and the far end of its `MessageChannel` went with it — so what
   * is left is a worker nobody can talk to, still holding whatever it holds. For
   * `im`'s wasm engine that is an exclusive OPFS access handle on its SQLite
   * files, and the next host's engine cannot open them while a ghost still has
   * them; the symptom is a workbench that reconnects fine and then insists there
   * is no active workspace.
   *
   * Ids make it worse and are why this cannot be left to chance: the host
   * allocates them from a counter that starts at 1 (见 `module-worker-driver`),
   * so a fresh host hands out 1 again and `hostedWorkers.set` would overwrite the
   * entry — dropping the only handle by which the old worker could ever be
   * terminated.
   */
  function discardHostedWorkers(): void {
    if (hostedWorkers.size === 0) {
      return;
    }
    logShell(
      'info',
      `terminating ${hostedWorkers.size} module worker(s) hosted for the lost ext-host`,
    );
    for (const worker of hostedWorkers.values()) {
      worker.terminate();
    }
    hostedWorkers.clear();
    hostedWorkerModules.clear();
  }

  /**
   * Open `url` in a new tab and report whether the browser allowed it (见 18 / 06).
   *
   * Do **not** pass `noopener` / `noreferrer` in `window.open`'s feature string:
   * those flags make the call return `null` even on success (the opener is denied
   * a handle on purpose), which falsely looks like a popup block and used to toast
   * "blocked" while the tab had already opened (见 21). Null out `opener` ourselves.
   */
  function openExternalWindow(url: string): boolean {
    const opened = window.open(url, '_blank');
    if (!opened) {
      return false;
    }
    try {
      opened.opener = null;
    } catch {
      // Browser may freeze opener access; the tab is still open.
    }
    return true;
  }

  /**
   * Perform `capabilities.openExternal` for the host (见 18 / 06).
   *
   * A new tab, and only that: the capability means "open this **outside** the app",
   * the browser counterpart of the desktop's `shell.openExternal`. It deliberately
   * does **not** fall back to navigating this tab when the popup is blocked —
   * replacing the workbench is a different intent, not a degraded version of this
   * one, and a flow that needs to leave and come back (OAuth) must ask for that
   * explicitly rather than rely on a popup blocker to get it (见 18 分端实现).
   *
   * A blocked popup is therefore a plain failure, logged rather than silently
   * papered over.
   */
  function openExternal(url: string): void {
    if (openExternalWindow(url)) {
      logShell('info', `openExternal: opened a new tab for ${url}`);
      return;
    }
    logShell(
      'warn',
      `openExternal: the browser blocked opening ${url} (popup blocker); nothing was opened`,
    );
  }

  const browserWindowRequestId = new URLSearchParams(
    window.location.search,
  ).get('browserRequest');
  const isBrowserShell =
    new URLSearchParams(window.location.search).get('shell') === 'browser';
  const browserWindowHandoff = createBrowserWindowHandoff({
    onError: (error) => {
      appendFromPage({
        level: 'warn',
        kind: 'log',
        message: 'Independent browser window handoff failed',
        error: {
          message: error instanceof Error ? error.message : String(error),
        },
      });
    },
  });

  const webBrowserHost = createWebBrowserHost({
    openExternal,
    editorTabs: createLocalStorageBrowserEditorTabCatalog(
      window.localStorage,
      (error) => {
        const message = error instanceof Error ? error.message : String(error);
        logShell(
          'error',
          `browser editor tab catalog persist failed: ${message}`,
        );
      },
    ),
    ...(isBrowserShell ? { presentation: 'window' as const } : {}),
  });
  const userActivation = createWebUserActivationApi({
    openBrowserWindow: (url, options) =>
      browserWindowHandoff.open(url, options),
  });
  window.hibroBrowser = webBrowserHost.chromeApi;
  if (isBrowserShell && browserWindowRequestId) {
    browserWindowHandoff.receive(browserWindowRequestId, (url) =>
      webBrowserHost.chromeApi.navigate(url),
    );
  }
  const detachedEditor = parseSurfaceTarget(window.location.search);
  if (
    detachedEditor?.viewId === BROWSER_EDITOR_VIEW_ID &&
    detachedEditor.instanceKey
  ) {
    webBrowserHost.seedEditorSlot(
      detachedEditor.instanceKey,
      detachedEditorUrlFromPayload(detachedEditor.payload),
    );
  }

  /**
   * Show `options.url` inside the app and settle the host's request (见 22).
   *
   * The whole of `capabilities.openBrowser` on this runtime, and the second half
   * of presenting an unowned link once a real browser tab has been ruled out.
   */
  function openInAppPreview(
    requestId: number,
    options: BrowserOpenOptions,
  ): void {
    void webBrowserHost
      .open(options)
      .then((slotId) => {
        deliverViewNavigation(
          BROWSER_EDITOR_VIEW_ID,
          { source: 'openBrowser' },
          slotId,
        );
        post({ type: 'hibro:open-browser-result', requestId });
      })
      .catch((error: unknown) => {
        const message = error instanceof Error ? error.message : String(error);
        if (options.presentation === 'window' && !isBrowserShell) {
          deliverViewNavigation(BROWSER_EDITOR_VIEW_ID, {
            source: 'openBrowser',
          });
        }
        appendFromPage({
          level: 'warn',
          kind: 'log',
          message: 'Web built-in browser open failed',
          error: { message },
        });
        post({
          type: 'hibro:open-browser-result',
          requestId,
          error: message,
        });
      });
  }

  /**
   * Perform `capabilities.saveStorageFile` for the host (见 18): hand one of a
   * module's own files to the user, the browser counterpart of the desktop's
   * save dialog.
   *
   * **Not gesture-bound, unlike `openExternal` above.** A download is not a
   * popup, so this survives arriving over RPC with no user activation — which is
   * exactly why the platform offers "save" and not "open" as the cross-runtime
   * shape. That also rules out `showSaveFilePicker`, which *does* require
   * activation (and only exists on Chromium).
   *
   * **A hidden iframe navigation, not `<a download>`.** The URL only means
   * anything to the `@storage` Service Worker — it is the SW that streams the
   * bytes out of OPFS and attaches the filename — so the request has to be one
   * the SW is guaranteed to intercept. Navigations are that guarantee; a
   * download initiated straight off an anchor is not reliably routed through it,
   * and when it escapes to the network there is no file there (見 18 部署: the
   * origin answers `/_…` misses with a 404). Same mechanism StreamSaver.js uses.
   * The response is an `attachment`, so the frame never commits a document.
   *
   * Nothing here touches the bytes: no `Blob`, no `arrayBuffer`, hence no size
   * class to special-case.
   *
   * Before the SW activates this 404s into a failed download the page cannot
   * observe (见 18 首屏窗口). Say so once rather than let it look like nothing
   * happened.
   */
  function saveFile(url: string): void {
    if (!navigator.serviceWorker?.controller) {
      logShell(
        'warn',
        `saveStorageFile: the @storage Service Worker has not claimed this page yet; the download will fail (${url})`,
      );
    }
    const frame = document.createElement('iframe');
    frame.hidden = true;
    frame.src = url;
    document.body.append(frame);
    // The browser takes the download over at response-headers time and the frame
    // never commits a document, so the frame's only job is to issue the request.
    // Removing it immediately would cancel that; a few seconds covers answering
    // from OPFS, and the transfer itself outlives the frame.
    window.setTimeout(() => frame.remove(), 10_000);
  }

  /**
   * In-app notification listeners, i.e. the mounted `NotificationToast`
   * (见 23). A set rather than a single slot because the workbench remounts on a
   * host reconnect and a stale subscriber must not keep the new one out.
   */
  const inAppNotificationListeners = new Set<
    (message: InAppNotificationMessage) => void
  >();

  function emitInAppNotification(message: InAppNotificationMessage): void {
    if (inAppNotificationListeners.size === 0) {
      // The host believes a tab is in the foreground but this page has no
      // workbench mounted (a detached-surface window, or one still booting), so
      // the notification has nowhere to go. Logged rather than swallowed: this is
      // the same class of disagreement the desktop driver warns about, and
      // silently losing a message is worse than a noisy record.
      logShell('warn', 'in-app notification arrived with nothing to show it');
      return;
    }
    for (const listener of inAppNotificationListeners) {
      listener(message);
    }
  }

  /**
   * Play the bundled chime when this delivery should sound (见 23). Same
   * listener as the toast, so desktop and web share one player. No listener
   * means this page has no workbench mounted to play it.
   */
  function cueNotificationSound(sound: boolean): void {
    if (!sound) {
      return;
    }
    if (inAppNotificationListeners.size === 0) {
      logShell('warn', 'notification sound dropped: no page to play it');
      return;
    }
    emitInAppNotification({ kind: 'sound' });
  }

  const inAppNotifications: InAppNotificationBridge = {
    subscribe: (listener) => {
      inAppNotificationListeners.add(listener);
      return () => inAppNotificationListeners.delete(listener);
    },
    reportClick: (tag) => {
      post({ type: 'hibro:notification-click', ...(tag ? { tag } : {}) });
    },
  };

  /**
   * OS notifications this page has shown, by tag, so 「已读即撤回」can close one
   * that is already sitting in the notification centre (需求 §4.4.3), and so a
   * later notification with the same tag can be re-announced rather than
   * silently swapped in (see {@link showOsNotification}).
   */
  const liveOsNotifications = new Map<string, Notification>();

  /**
   * Raise an OS notification for the host (见 18 / 23), the browser counterpart of
   * the desktop's main-process notification driver.
   *
   * Permission is **read, never requested**: `Notification.requestPermission()`
   * requires a user gesture on Safari, and this arrives from a module over RPC —
   * long out of any gesture's call stack, exactly like the popup blocker above.
   * So a missing grant is a plain, logged failure rather than a prompt that would
   * be silently ignored. Granting it is the job of the settings section backed by
   * {@link osNotificationBridge}, which runs inside a real gesture.
   *
   * Neither the visibility gate nor the click destination is here: the host chose
   * this surface over an in-app toast, and it owns where a click goes (见 23) — the
   * page only reports the tag back.
   */
  function showOsNotification(notification: ResolvedNotification): void {
    // The OS toast is silent, so this chime is the only sound. It plays only
    // when the browser will actually show the notification (见 23).
    const permission = !('Notification' in window)
      ? 'missing'
      : Notification.permission;
    const chime = shouldPlayNotificationChime({
      sound: notification.sound,
      surface: 'os',
      os: webOsNotificationDelivery(permission),
    });
    if (permission === 'missing') {
      logShell('warn', 'notifyUser: this browser has no Notification API');
      return;
    }
    if (permission !== 'granted') {
      logShell(
        'warn',
        `notifyUser: notification permission is "${permission}"; nothing was shown`,
      );
      return;
    }
    if (chime) {
      cueNotificationSound(true);
    }
    const { title, body, tag } = notification;
    // Withdraw the previous notification carrying this tag before raising the new
    // one, exactly as the desktop driver does. Handing the same `tag` to the
    // constructor does coalesce, but the spec's replace path shows the new
    // notification *without renotifying* — no popup, no sound, just the centre
    // entry rewritten. So a chat's second message onwards went unannounced until
    // the user cleared the first one. (`renotify` is the switch meant for this,
    // but browsers only honour it for service-worker notifications.)
    if (tag) {
      liveOsNotifications.get(tag)?.close();
      liveOsNotifications.delete(tag);
    }
    const shown = new Notification(title, {
      body,
      // The page plays the bundled chime. A sounding browser notification
      // would ring a second time (见 23 声音).
      silent: true,
      ...(tag ? { tag } : {}),
    });
    if (tag) {
      liveOsNotifications.set(tag, shown);
      shown.onclose = () => {
        if (liveOsNotifications.get(tag) === shown) {
          liveOsNotifications.delete(tag);
        }
      };
    }
    shown.onclick = () => {
      window.focus();
      post({ type: 'hibro:notification-click', ...(tag ? { tag } : {}) });
    };
  }

  /** Withdraw an OS notification this page showed (需求 §4.4.3). */
  function dismissOsNotification(tag: string): void {
    const live = liveOsNotifications.get(tag);
    liveOsNotifications.delete(tag);
    live?.close();
  }

  const shell = bootShell({
    container,
    runtime: 'web',
    uiLoaders: discoverUiLoaders(),
    log: (message) => logShell('info', message),
    userActivation,
    // `iframe` views get their gated port straight from this page (见 18): no
    // main process to broker through, so we hand the host one end of a fresh
    // channel and `IframeSurface` hands the other to the frame. Closes over
    // `currentPort` so a reconnect keeps working without rebuilding the bridge.
    iframeBridge: createWebIframeBridge(
      (viewId, hostPort, instanceKey, hostedIn) => {
        currentPort?.postMessage(
          { type: 'hibro:connect-iframe', viewId, instanceKey, hostedIn },
          [hostPort],
        );
      },
    ),
    // Diagnostics read the page sink directly — no IPC hop, since the sink is in
    // this very context (见 15). Exporting still works via "copy as JSONL".
    logBridge,
    // The settings control that grants OS notifications (见 18 / §5.15). It only
    // exists on this runtime, and only as a settings control, because
    // `requestPermission()` is gesture-bound while `notifyOS` arrives over RPC —
    // so the prompt cannot live where the notification is raised. Absent
    // entirely when the browser has no Notification API, which hides the section
    // rather than offering a button that cannot work.
    ...('Notification' in window
      ? {
          osNotificationBridge: {
            permission: () => Notification.permission,
            // Contractually non-rejecting: `requestPermission` can throw in an
            // insecure context or under an embedding policy, and the sink lives
            // here, not in `platform-renderer`. Report it and answer with the
            // state that is actually in force.
            request: () =>
              Notification.requestPermission().catch((err: unknown) => {
                logShell(
                  'warn',
                  `notification permission request failed: ${err instanceof Error ? err.message : String(err)}`,
                );
                return Notification.permission;
              }),
          },
        }
      : {}),
    // In-app notifications routed here by the host (见 23), plus the click report
    // that lets it resolve where they go. Unconditional, unlike the permission
    // bridge above: an in-app toast needs no grant, which is exactly why it is the
    // surface for a user who is present.
    inAppNotifications,
    // Load-unpacked dev modules (见 11 调试 / 18). Lives on this side because
    // every verb is gesture-bound: only a click may open the directory picker,
    // and only a click may re-request a lapsed read grant — neither of which a
    // worker can ever do. Absent on a browser with no File System Access API,
    // which hides the section rather than offering a button that cannot work.
    ...('showDirectoryPicker' in window
      ? {
          linkedModulesBridge: createLinkedModules({
            // No list rides along: the copies are in OPFS, which the host reads
            // itself (见 `hibro:linked-modules-changed`).
            onChanged: () => post({ type: 'hibro:linked-modules-changed' }),
            log: (message, error) =>
              logShell(
                'warn',
                error instanceof Error
                  ? `${message}: ${error.message}`
                  : message,
              ),
          }),
        }
      : {}),
    // Local install into the content-addressed cache (见 12). Separate from
    // Link above: this is "install this .tgz / delivered root", not a
    // working-directory mount. The picker stays here so the click is still
    // a live gesture.
    ...(canPickLocalBundle() ? { pickLocalBundle } : {}),
    // Gesture-bound open for `openUrl` launch items (见 21 / 18). Must stay
    // synchronous in the click path — routing through the extension host would
    // drop the user gesture and the popup blocker would reject the open.
    openExternalBridge: {
      open: (url) => {
        if (openExternalWindow(url)) {
          logShell('info', `openExternal: opened a new tab for ${url}`);
          return true;
        }
        logShell(
          'warn',
          `openExternal: the browser blocked opening ${url} (popup blocker); nothing was opened`,
        );
        return false;
      },
    },
  });

  const startupUrlBarrier = createStartupUrlBarrier<MessagePort>((port) => {
    if (disposed || port !== currentPort) {
      return;
    }
    shell.connect(createTransport(port));
  });

  // Tell the host when an in-page hosted surface goes away (见 18 弹窗拦截) — the
  // counterpart of a detached window's connection dropping, which a floating
  // frame has no connection to signal with. Fires for the user closing the frame
  // and for the host having asked, and the host tolerates the latter being an
  // echo (its entry is dropped when the surface reports itself gone, not when it
  // asked, exactly as for a real window).
  shell.inlineDetached.onClosed((name) => {
    post({ type: 'hibro:detach-inline-closed', name });
  });

  function clearReadyTimer(): void {
    if (readyTimer !== undefined) {
      clearTimeout(readyTimer);
      readyTimer = undefined;
    }
  }

  function clearReconnectTimer(): void {
    if (reconnectTimer !== undefined) {
      clearTimeout(reconnectTimer);
      reconnectTimer = undefined;
    }
  }

  function clearReconnectStableTimer(): void {
    if (reconnectStableTimer !== undefined) {
      clearTimeout(reconnectStableTimer);
      reconnectStableTimer = undefined;
    }
  }

  /**
   * A build other than this page's is deployed, and the host has stepped down
   * (见 18 版本换代). Reloading is the whole treatment: this page comes back as
   * the new generation, and whichever page reloads first brings up the fresh
   * host that all of them then share.
   *
   * `disposed` is set for the same reason a lost host sets it — the port is about
   * to die, and neither the RPC client nor the retry path should act on it. It
   * also means the ready-timeout cannot fire behind the reload.
   *
   * The retiring host will drop its liveness beacon a task later, so this page is
   * about to be told its host died; `disposed` is what keeps that from being
   * answered with a reconnect, which would race the reload and land on the old
   * generation again.
   */
  function handleGenerationChanged(hostBuildId: string): void {
    if (disposed) {
      return;
    }
    disposed = true;
    clearReadyTimer();
    clearReconnectTimer();
    clearReconnectStableTimer();
    // The retiring host is about to `self.close()`, so its workers are ghosts
    // from here on. Mostly redundant with the reload below tearing this document
    // down, but the `stuck` branch does *not* reload — it leaves this page up
    // with no host, which is exactly the case they must not survive.
    discardHostedWorkers();
    const action = decideGenerationAction({
      pageBuildId: PAGE_BUILD_ID,
      hostBuildId,
      store: sessionStore(),
      now: Date.now(),
    });
    if (action === 'reload') {
      logShell(
        'info',
        `ext-host generation changed (page ${PAGE_BUILD_ID}, host ${hostBuildId}); reloading`,
      );
      shell.showConnecting();
      window.location.reload();
      return;
    }
    // Reloaded once for this exact pair and came back to it: the origin is
    // serving files from two deploys, so another reload would loop.
    logShell(
      'error',
      `reload did not pick up the new build (page ${PAGE_BUILD_ID}, host ${hostBuildId})`,
    );
    shell.showDisconnected(
      'A new version is deployed but this tab keeps loading the old one. ' +
        'Force-reload (Ctrl/Cmd+Shift+R) or clear this site\u2019s cache.',
      () => window.location.reload(),
    );
  }

  /**
   * The host is gone (见 18 连接生命周期). Try to get it back, and only once that
   * has failed often enough hand the tab to the user.
   *
   * Reconnecting on its own is not a way of hiding the failure: every loss is
   * logged at `error` before anything is retried, so the diagnostics panel shows
   * how often this tab has been dropped even when the user never noticed, and a
   * host that stays down still ends on the explicit disconnected screen.
   *
   * The connecting placeholder goes up on the first attempt rather than leaving
   * the stale workbench painted. Nothing is lost by it: `shell.connect` closes the
   * old `ChannelClient` and re-renders from scratch, so the tree cannot survive a
   * reconnect either way — and leaving it up would be a lie, since every control
   * in it is wired to a port whose peer no longer exists.
   */
  function handleHostLost(reason: string): void {
    if (disposed) {
      return;
    }
    disposed = true;
    // Stop handing records to a port whose worker is gone. Later lines queue
    // until the replacement host connects and appends them itself.
    setLogPort(undefined);
    failLogFlushes('log file flush failed: extension host disconnected');
    clearReadyTimer();
    clearReconnectTimer();
    clearReconnectStableTimer();
    logShell('error', reason);
    // Before anything is retried, and regardless of whether it will be: these
    // are dead weight either way, and the next host cannot start cleanly while
    // they are still holding their storage.
    discardHostedWorkers();

    const decision = decideReconnect({ attempt: reconnectAttempt });
    if (decision.kind === 'give-up') {
      shell.showDisconnected(reason, () => {
        // A hand-clicked retry is a fresh start, not attempt six.
        reconnectAttempt = 0;
        reconnect();
      });
      return;
    }

    reconnectAttempt += 1;
    logShell(
      'info',
      `reconnecting to the ext-host in ${decision.delayMs}ms (attempt ${reconnectAttempt})`,
    );
    shell.showConnecting();
    reconnectTimer = setTimeout(() => {
      reconnectTimer = undefined;
      reconnect();
    }, decision.delayMs);
  }

  function reconnect(): void {
    disposed = false;
    shell.showConnecting();
    connectToHost();
  }

  /**
   * Watch this host's liveness beacon, the only signal that says the SharedWorker
   * died (见 18 连接生命周期 / `hostLivenessLockName`).
   *
   * Everything else the page has is boot-time only: `worker.onerror` covers a
   * script that fails to load, and the ready timeout is cleared the moment we get
   * here. Without this a worker the browser reclaims — after sleep, under memory
   * pressure — leaves the workbench painted over a port that swallows every
   * message, and each RPC hangs unsettled.
   *
   * A grant can arrive long after the fact, because a hidden or frozen tab runs
   * the callback only when it is next allowed to. So the answer is checked against
   * the connection it was asked about: by then this page may already be on a
   * second host (whose beacon has a different name and its own watch), or on its
   * way out through the generation reload, and in both cases the death being
   * reported is old news.
   */
  function beginLivenessWatch(
    port: MessagePort,
    hostLockName: string | undefined,
  ): void {
    if (hostLockName === undefined) {
      // The host said it could not take its own beacon, and it logged why. Say
      // what that costs *here* rather than looking healthy: for this connection
      // the page is back to having no way to notice a dead host.
      logShell(
        'warn',
        'ext-host published no liveness beacon; a host that dies will not be detected on this connection',
      );
      return;
    }

    watchHostLiveness({
      locks: navigator.locks,
      name: hostLockName,
      onReleased: () => {
        if (disposed || port !== currentPort) {
          return;
        }
        handleHostLost(
          'Extension host SharedWorker is gone: it released its liveness lock.',
        );
      },
      onUnavailable: (reason) => {
        logShell(
          'warn',
          `ext-host liveness watch could not be set up: ${reason}`,
        );
      },
    });
  }

  function createTransport(port: MessagePort): IMessageTransport {
    return {
      send: (message: RpcMessage) => port.postMessage(message),
      onMessage: (handler: (message: RpcMessage) => void) => {
        const listener = (event: MessageEvent): void => {
          if (isControlMessage(event.data)) {
            return;
          }
          handler(event.data as RpcMessage);
        };
        port.addEventListener('message', listener);
        return () => port.removeEventListener('message', listener);
      },
    };
  }

  function wireControlPlane(port: MessagePort): void {
    port.addEventListener('message', (event: MessageEvent) => {
      if (disposed || port !== currentPort) {
        return;
      }
      const data = event.data as HostToPageMessage | null;
      switch (data?.type) {
        case 'hibro:log':
          appendLog(data.record);
          break;
        case 'hibro:log-flushed':
          settleLogFlush(data.requestId);
          break;
        case 'hibro:connections':
          logShell('info', `ext-host connections: ${data.count}`);
          break;
        case 'hibro:ready':
          clearReadyTimer();
          logShell('info', 'ext-host ready');
          beginLivenessWatch(port, data.hostLockName);
          clearReconnectStableTimer();
          reconnectStableTimer = setTimeout(() => {
            reconnectStableTimer = undefined;
            if (disposed || port !== currentPort) {
              return;
            }
            reconnectAttempt = 0;
            logShell(
              'info',
              `ext-host remained healthy for ${String(RECONNECT_STABLE_MS)}ms; reconnect budget reset`,
            );
          }, RECONNECT_STABLE_MS);
          break;
        case 'hibro:startup-url-delivered':
          startupUrlBarrier.release(port);
          break;
        case 'hibro:generation-changed':
          handleGenerationChanged(data.hostBuildId);
          break;
        case 'hibro:open-external':
          openExternal(data.url);
          break;
        case 'hibro:open-browser':
          openInAppPreview(data.requestId, data.options);
          break;
        case 'hibro:open-unowned-link': {
          // Nobody claimed this address, so the runtime picks how to show it
          // (见 20 第三档). A real tab beats the in-app `<iframe>` whenever the
          // click's activation is still alive, because that iframe is what any
          // site may refuse outright — see `presentUnownedLink`.
          const presentation = presentUnownedLink(data.url, {
            hasUserActivation: hasLiveUserActivation,
            openNewTab: openExternalWindow,
          });
          const link = describeLinkForLog(data.url);
          if (presentation.where === 'new-tab') {
            logShell('info', `openLink: opened a new tab for ${link}`);
            post({
              type: 'hibro:open-browser-result',
              requestId: data.requestId,
            });
            break;
          }
          logShell(
            'info',
            `openLink: showing ${link} in the in-app preview instead of a new tab (${presentation.reason})`,
          );
          openInAppPreview(data.requestId, { url: data.url });
          break;
        }
        case 'hibro:navigate':
          // The inline-dom half of a `view:<id>[::<key>]` navigation (见 8); the
          // host delivered the gated-iframe half itself. Routed to the mounted
          // view instance, or buffered by the registry until it mounts.
          deliverViewNavigation(data.viewId, data.location, data.instanceKey);
          break;
        case 'hibro:set-view-title':
          // An iframe page renamed its own tab (见 3 / 11). The workbench writes
          // it onto that one panel; a page that does not hold the panel no-ops.
          deliverViewTitle(data.viewId, data.instanceKey, data.title);
          break;
        case 'hibro:notify-os':
          showOsNotification(data.notification);
          break;
        case 'hibro:notify-in-app':
          // The host routed here because a tab is in the foreground; the toast is
          // painted by the same `NotificationToast` the desktop uses, reached
          // through the bridge below rather than a second rendering path.
          emitInAppNotification({
            kind: 'show',
            title: data.notification.title,
            body: data.notification.body,
            ...(data.notification.tag ? { tag: data.notification.tag } : {}),
          });
          cueNotificationSound(data.notification.sound);
          break;
        case 'hibro:notify-dismiss':
          // Broadcast, so this page may hold either surface, neither, or both.
          dismissOsNotification(data.tag);
          emitInAppNotification({ kind: 'dismiss', tag: data.tag });
          break;
        case 'hibro:attention-state':
          attentionPainter.setState(data.state);
          break;
        case 'hibro:attention-pulse':
          attentionPainter.pulse(data.urgency);
          break;
        case 'hibro:attention-clear-pulse':
          attentionPainter.clearPulse();
          break;
        case 'hibro:save-file':
          saveFile(data.url);
          break;
        case 'hibro:detach-open':
          post({
            type: 'hibro:detach-opened',
            requestId: data.requestId,
            placement: showDetachedSurface(data, shell.inlineDetached),
          });
          break;
        case 'hibro:detach-focus':
          focusDetachedWindow(data.name);
          break;
        case 'hibro:detach-focus-self':
          window.focus();
          break;
        case 'hibro:detach-inline-close':
          // Unmounting reports the close back to the host (see the store), which
          // is what drops its registry entry — the same "the surface itself says
          // it is gone" rule a real detached window follows.
          shell.inlineDetached.unmount(data.name);
          break;
        case 'hibro:detach-inline-raise':
          shell.inlineDetached.raise(data.name);
          break;
        case 'hibro:spawn-worker':
          spawnQueue = spawnQueue.then(() =>
            spawnModuleWorker(
              data.workerId,
              data.moduleId,
              data.url,
              data.exclusive === true,
            ).then(
              () => undefined,
              () => undefined,
            ),
          );
          break;
        case 'hibro:terminate-worker':
          terminateModuleWorker(data.workerId);
          break;
        case 'hibro:detach-close-self':
          // This page *is* the detached window; closing it drops its connection,
          // which is how the host learns the surface came back (§5.8).
          window.close();
          break;
        default:
          break;
      }
    });
  }

  /**
   * Liveness handshake (见 18 / 20): hold a Web Lock, forward the launch URL on
   * the first connect only, and keep visibility current.
   *
   * Reconnect deliberately does **not** re-deliver a deep link — the address bar
   * was already reset after the first hand-off, and an OAuth `code` is single-use
   * (见 20). Passing the origin root keeps `deliverUrl` a no-op.
   */
  function beginHandshake(port: MessagePort): void {
    const lockName = connectionLockName(
      globalThis.crypto?.randomUUID?.() ?? String(Math.random()).slice(2),
    );
    // Announce the name only once the lock is actually held — see
    // {@link holdConnectionLock} for the race this avoids.
    holdConnectionLock(navigator.locks, lockName, () => {
      announce(port, lockName);
    });

    readyTimer = setTimeout(() => {
      handleHostLost(
        'Extension host did not become ready in time. The SharedWorker may have failed to start.',
      );
    }, HOST_READY_TIMEOUT_MS);
  }

  /** Introduce this page to the host, now that its liveness lock is held. */
  function announce(port: MessagePort, lockName: string): void {
    if (disposed || port !== currentPort) {
      return;
    }

    const href = initialHandshake
      ? window.location.href
      : `${window.location.origin}/`;
    port.postMessage({
      type: 'hibro:hello',
      lockName,
      href,
      visible: document.visibilityState === 'visible',
      buildId: PAGE_BUILD_ID,
    } satisfies PageToHostMessage);

    if (initialHandshake) {
      initialHandshake = false;
      // A deep link is a one-time **activation**, not an address-bar state (the
      // `vscode://` semantics), so reset to the root once it has been handed over.
      // Two reasons beyond tidiness: the callback query may carry a one-time
      // credential that must not linger in the address bar or history, and leaving
      // the path would re-deliver a payload-less link on every refresh. ("Address
      // bar reflects the current view" is a separate feature — history sync —
      // deliberately not conflated with this one.)
      //
      // Only when the URL actually *is* a deep link: a detached window carries its
      // target in `?surface=&module=` on the root path (见 8), and blanking that
      // would turn it into a full workbench on reload.
      //
      // `APP_SCHEME` here is a placeholder, not a channelization miss: on web the
      // page URL is always http(s), so `parseModuleLink`'s custom-scheme branch
      // never runs — only the path-shaped `/<moduleId>/…` branch matters.
      if (parseModuleLink(window.location.href, { scheme: APP_SCHEME })) {
        window.history.replaceState(null, '', '/');
      }
    }

    // OS-facing signals the worker cannot read itself (no `matchMedia` there).
    const darkQuery = window.matchMedia('(prefers-color-scheme: dark)');
    port.postMessage({
      type: 'hibro:os-theme',
      osDark: darkQuery.matches,
    } satisfies PageToHostMessage);
    port.postMessage({
      type: 'hibro:os-locale',
      locale: navigator.language,
    } satisfies PageToHostMessage);
  }

  function connectToHost(): void {
    clearReadyTimer();
    clearReconnectStableTimer();
    // One SharedWorker for every tab of this origin (架构方案 §4.3): the host is a
    // single instance and each tab is just another connection, mirroring the
    // desktop utility process serving all windows. A reconnect constructs a new
    // one (and a new name would also work, but the fixed name is what lets a
    // healthy second tab join the surviving worker — after a crash every tab
    // rebuilding with the same name is fine: the browser starts a fresh instance
    // when none is alive).
    //
    // The *URL* is load-bearing too, and unlike the name it is not visible here:
    // the build pins this worker to a hash-free `/_ext-host.js` (见 18 版本换代).
    // Reuse is keyed on `(origin, url, name)`, so a content-hashed URL would make
    // each deploy run a second host beside the one an older tab still holds.
    const worker = new SharedWorker(
      new URL('../ext-host-web/index.ts', import.meta.url),
      { type: 'module', name: 'hibro-ext-host' },
    );
    const port = worker.port;
    currentPort = port;
    wireControlPlane(port);
    port.start();
    // After the control listener is attached, so a flush reply cannot arrive
    // before `settleLogFlush` is listening. The worker queues the append until
    // it starts its own end of the port.
    setLogPort(port);

    worker.addEventListener('error', () => {
      handleHostLost('Extension host SharedWorker failed to load or crashed.');
    });

    startupUrlBarrier.stage(port);
    beginHandshake(port);
  }

  // Keep the host's view of "is the user looking at the app" current (见 18). It
  // cannot observe this itself, and with several tabs no single page can answer
  // it either — the host aggregates across connections, the way the desktop asks
  // its one main window `isFocused()`.
  document.addEventListener('visibilitychange', () => {
    const visible = document.visibilityState === 'visible';
    post({ type: 'hibro:visibility', visible });
    // Also logged, not just relayed: this is the timeline a "it stopped working
    // overnight" report has to be read against. See the freeze/resume pair below
    // for why the host's aggregated view cannot answer that on its own.
    logShell('info', `page became ${visible ? 'visible' : 'hidden'}`);
  });

  // Chromium freezes and can discard a background tab (Page Lifecycle), and
  // **that is not symmetric with the host**: a SharedWorker is not a tab, so it
  // keeps its timers and its connections while a page is frozen. Anything a
  // module runs *in the page* does not — including a `DedicatedWorker`, which is
  // where a module puts work needing OPFS sync access handles (`im`'s engine, 见
  // 19). So the two halves of a module can be in different states, and the only
  // report that reaches us is "it was fine last night and dead this morning".
  //
  // These two records are what make that report readable: `freeze` bounds when
  // the page stopped executing and `resume` says it came back. Neither event is
  // observable from the host, which is why the page logs them itself.
  //
  // The memory ring still dies with the page, so a discard opens on an empty
  // panel. The daily file in OPFS keeps what the worker had already flushed;
  // the batch still queued here or inside the worker does not (见 15).
  document.addEventListener('freeze', () => {
    logShell(
      'info',
      'page frozen by the browser; timers and in-page workers are suspended until resume',
    );
  });
  document.addEventListener('resume', () => {
    logShell('info', 'page resumed from frozen');
  });

  // Fast path for a graceful unload; the Web Lock above is what actually
  // guarantees cleanup. `pagehide` rather than `beforeunload` (the latter is
  // unreliable on mobile and suppresses bfcache) — but even `pagehide` may not be
  // delivered to the worker before a closing tab is torn down, which is exactly
  // why the lock exists.
  window.addEventListener('pagehide', () => {
    browserWindowHandoff.dispose();
    webBrowserHost.dispose();
    post({ type: 'hibro:bye' });
  });

  // Ask the browser not to evict this origin's storage (见 07 配额 / 18). Module
  // files live in OPFS and platform KV in IndexedDB; without persistence both are
  // evictable under storage pressure, and the module would simply find its data
  // gone. This is the page's job because `persist()` is **Window-only** — the
  // worker's `StorageManager` has `estimate()`/`persisted()` but no `persist()` —
  // same reason the page owns `matchMedia` and `navigator.language` above.
  void navigator.storage
    ?.persist?.()
    .then((granted) => {
      logShell('info', `storage persistence granted: ${granted}`);
    })
    .catch((err: unknown) => {
      logShell(
        'warn',
        `storage persistence request failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    });

  // Theme change listener is page-lifetime (not per-connection): each handshake
  // also pushes the current value, so a reconnect is covered; this only needs to
  // fire on subsequent flips.
  window
    .matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', (event) => {
      post({ type: 'hibro:os-theme', osDark: event.matches });
    });

  connectToHost();
}
